import { createEffect, onCleanup, onMount } from "solid-js"
import { useServer } from "@/context/server"
import {
  clearStarterMessageServer,
  configureStarterMessages,
  refreshStarterMessages,
} from "@/context/session/starters"

export function createStarterMessageLifecycle(input: {
  configure: (server: NonNullable<ReturnType<typeof useServer>["current"]>["http"]) => Promise<void>
  refresh: () => Promise<void>
  clear: (server?: NonNullable<ReturnType<typeof useServer>["current"]>["http"]) => void
}) {
  let generation = 0
  let current: NonNullable<ReturnType<typeof useServer>["current"]>["http"] | undefined
  let disposed = false

  return {
    switch(server: typeof current) {
      const previous = current
      current = server
      const active = ++generation
      if (previous && previous !== server) input.clear(previous)
      if (!server) return Promise.resolve()
      return input.configure(server).then(
        () => undefined,
        () => undefined,
      ).then(() => {
        if (disposed || active !== generation || current !== server) return
      })
    },
    refresh() {
      if (disposed || !current) return Promise.resolve()
      const active = generation
      return input.refresh().catch(() => undefined).then(() => {
        if (disposed || active !== generation) return
      })
    },
    dispose() {
      disposed = true
      generation += 1
      input.clear(current)
      current = undefined
    },
  }
}

export function StarterMessageController() {
  const server = useServer()
  const lifecycle = createStarterMessageLifecycle({
    configure: configureStarterMessages,
    refresh: refreshStarterMessages,
    clear: clearStarterMessageServer,
  })

  createEffect(() => {
    void lifecycle.switch(server.current?.http)
  })
  onMount(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") void lifecycle.refresh()
    }
    document.addEventListener("visibilitychange", refresh)
    onCleanup(() => {
      document.removeEventListener("visibilitychange", refresh)
    })
  })
  onCleanup(() => lifecycle.dispose())
  return null
}
